import React, { createContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import useHttp from "../hooks/useHttp.jsx";
import useServices from "../hooks/useServices";
import { getUrlSearchParam, hasUrlSearchParam, normalizeUrlSearchParams } from "../pages/utils/commonUtils";
import { useScript } from "./../hooks/Hooks";
import { useRolesContext } from "./RolesContext.js";
import { hasAiFeaturesCatalogContext, useAiFeaturesStore } from "../stores/aiFeaturesStore";
import { syncUserDetails } from "../stores/userDetailsStore";

export const AppContext = createContext();

export const ProviderObj = (props) => {
    normalizeUrlSearchParams();
    const { fetchInstalledServices, getAllTextSMSServices } = useServices();
    const setLoading = props.setLoading;
    const [rolesProperties, setRolesProperties] = useRolesContext();
    const setOtpModalState = props.setOtpModalState;
    const setBlockInteractionModalProps = props.setBlockInteractionModalProps;
    const notificationAPI = props.notificationAPI;
    const setPermissionDeniedToSubAccount = props.setPermissionDeniedToSubAccount;
    const setAppPropsObject = props.setAppPropsObject;
    const setAccessDenied = props.setAccessDenied;
    const accessDenied = props.accessDenied;
    const setShowCaptchaWidget = props.setShowCaptchaWidget;
    const query = new URLSearchParams(window.location.search);
    const [appProperties, setAppProperties] = useState({});
    const [scriptUrl, setScriptUrl] = useState("");
    const [hasExternalScripts, setHasExternalScripts] = useState(false);
    const [initFunctionCall, setInitFunctionCall] = useState("");
    const appPropertiesObject = useMemo(() => [appProperties, setAppProperties], [appProperties, setAppProperties]);
    const previousOsyncIdRef = useRef();

    useLayoutEffect(() => {
        syncUserDetails({
            users: appProperties?.licenseObj?.allLicensedUserDetails || appProperties?.allLicensedUserDetails || [],
            nameDisplayFormat: appProperties?.nameDisplayFormat,
            currentUser: appProperties?.currentUser,
        });
    }, [
        appProperties?.licenseObj?.allLicensedUserDetails,
        appProperties?.allLicensedUserDetails,
        appProperties?.nameDisplayFormat,
        appProperties?.currentUser,
    ]);

    let path = window.location.pathname;
    var serviceFromUrl = query.get("service");
    if (path === "/zoho-crm/webhook") {
        serviceFromUrl = "zohocrm";
    }

    const serviceName = serviceFromUrl != null ? serviceFromUrl : "default";

    const { fetchData } = useHttp();

    useEffect(() => {
        if (!hasAiFeaturesCatalogContext(appProperties)) {
            return;
        }
        const store = useAiFeaturesStore.getState();
        if (previousOsyncIdRef.current && previousOsyncIdRef.current !== appProperties.osyncId) {
            store.clearCatalog();
        }
        previousOsyncIdRef.current = appProperties.osyncId;
        void store.ensureCatalogLoaded(fetchData, appProperties);
    }, [fetchData, appProperties, appProperties?.osyncId, appProperties?.companyId, appProperties?.userId]);

    const [serviceObject, setServiceObj] = useState({});
    const [userData, setUserData] = useState({});

    let appProps = {
        controller: serviceObject,
        setOtpModalState: setOtpModalState,
        setBlockInteractionModalProps: setBlockInteractionModalProps,
        setPermissionDeniedToSubAccount: setPermissionDeniedToSubAccount,
        notificationAPI: notificationAPI,
        setAccessDenied: setAccessDenied,
        accessDenied: accessDenied,
        setShowCaptchaWidget: setShowCaptchaWidget,
        setLoading: setLoading,
    };
    useEffect(() => {
        if (serviceName !== null && serviceName !== undefined) {
            getController(serviceName).then((serviceObj) => {
                serviceObj.loadScripts?.().then((response) => {
                    if (response) {
                        setScriptUrl(response);
                        setHasExternalScripts(true);
                    } else {
                        setHasExternalScripts(false);
                    }
                });
                setServiceObj(serviceObj);
                appProps = {
                    controller: serviceObj,
                    leftServiceName: serviceObj?.service,
                    setOtpModalState: setOtpModalState,
                    setLoading: setLoading,
                };
                setAppProperties((prev) => {
                    return { ...prev, ...appProps };
                });
            });
        }
    }, [serviceName]);

    var scriptToBeLoaded = useScript(scriptUrl);
    useEffect(() => {
        if (serviceObject) {
            if (scriptUrl) {
                if (scriptToBeLoaded === "ready") {
                    setInitFunctionCall("scripts-from-controller-ready");
                }
            } else {
                setInitFunctionCall("no-scripts-from-controller");
            }
        }
    }, [scriptToBeLoaded, serviceObject, scriptUrl]);

    useEffect(() => {
        var urlParams = new URLSearchParams(window.location.search);

        if (urlParams.get("error")) {
            setLoading(false);
            return;
        }
        // Public website auth pages have no CRM params; license bootstrap rejects undefined and hits Bugsnag.
        if (window.location.pathname.startsWith("/web/")) {
            setLoading(false);
            return;
        }
        if (
            !isEmpty(serviceObject) &&
            ((initFunctionCall === "scripts-from-controller-ready" && hasExternalScripts) || !hasExternalScripts)
        ) {
            setLoading(true);
            serviceObject.init(setAppProperties).then(function (data) {
                appProps.module = data?.module || data?.Entity;
                serviceObject.getUserData(true).then(async (extensionData) => {
                    appProps.companyId = extensionData?.companyId;
                    appProps.userId = extensionData?.userId;

                    if (extensionData?.userHash) {
                        appProps.userHash = extensionData?.userHash;
                    }

                    appProps.setLoading = setLoading;
                    const installResult = await serviceObject.ensureInstalled(fetchData, appProps);
                    if (installResult?.accessDenied) {
                        setPermissionDeniedToSubAccount(true);
                        return;
                    }
                    serviceObject
                        .checkIsLicensed(fetchInstalledServices, appProps, getAllTextSMSServices)
                        .then(async function (response) {
                            if (response) {
                                const {
                                    parseExcludedFeatureAndConstructSet,
                                    parseIncludedFeatureAndConstructSet,
                                    resolveNameDisplayFormat,
                                } = await import("../constants/AppConstants.js");
                                const { ProviderCommonObj } = await import("../constants/ProviderConstants.js");
                                const { capitalizeFirstLetter, getCurrentUserDetails } =
                                    await import("../pages/utils/commonUtils.js");

                                const responseData = response?.data;
                                if (responseData?.hash !== null && responseData?.hash !== undefined) {
                                    appProps.hash = responseData?.hash;
                                }
                                appProps.isParentOsyncAccount = false;
                                if (ProviderCommonObj) {
                                    appProps.providerCommonObj = Object.assign({}, ProviderCommonObj);
                                }

                                const licenseObject = responseData?.license;
                                const rolesBasedObject = responseData?.roles;
                                appProps.settings = responseData?.settings || [];
                                appProps.excludedFeatures = parseExcludedFeatureAndConstructSet(responseData?.settings);
                                appProps.includedFeatures = parseIncludedFeatureAndConstructSet(responseData?.settings);
                                appProps.nameDisplayFormat = resolveNameDisplayFormat(responseData?.settings);
                                const currentRoleDetails = responseData?.roleAccessDetails;
                                appProps.alerts = responseData?.alerts || [];
                                appProps.defaultPhoneNumber = responseData?.defaultPhoneNumber || "";
                                appProps.enums = responseData?.enums || {};
                                appProps.apiKeysModuleEnabled = responseData?.apiKeysModuleEnabled || false;
                                setRolesProperties((prev) => ({
                                    ...prev,
                                    rolesBasedObject: rolesBasedObject,
                                    currentRoleDetails: currentRoleDetails,
                                    showExternalPageRestrictionPermission:
                                        responseData?.showExternalPageRestrictionPermission || false,
                                }));
                                appProps.licenseObj = licenseObject;
                                if (responseData?.apps) {
                                    appProps.apps = responseData?.apps;
                                }
                                if (responseData?.leftServiceClientDetails) {
                                    appProps.leftServiceClientDetails = responseData?.leftServiceClientDetails;
                                    appProps.leftServiceId = appProps.leftServiceClientDetails?.serviceId;
                                }

                                // let integDetails = responses?.integrations
                                // let integDetails = responseData?.integrations?.filter((item) => item?.integProps);
                                const leftAuth = responseData?.left;
                                if (leftAuth) appProps.authCRM = leftAuth?.auth?.authorized;
                                let integrationDetails = responseData?.integrationDetails;

                                let account = responseData?.accountDetails;
                                appProps.currentUser = responseData?.currentUser;
                                appProps.licenseHandledBy = responseData?.accountDetails?.licenseHandledBy;
                                if (account) {
                                    appProps.accountStatus = account?.accountStatus;
                                    appProps.osyncId = account?.osyncId;
                                    appProps.extensionId = account?.extensionId;
                                    if (account?.hash) {
                                        appProps.hash = account?.hash;
                                    }
                                    appProps.isParentOsyncAccount = account?.isParentHelloSendAccount === 1;
                                    appProps.agencyAccount = account?.agencyAccount === 1;
                                    appProps.extensionInstalledUserEmailId = account?.email;
                                    appProps.surveyTaken = account?.isSurveyTaken || false;
                                    appProps.extensionInstalledApps = account?.installedApps;
                                    appProps.domainName = account?.domainName;
                                }

                                appProps.actionRequiredItems = responseData?.actionRequiredItems ?? [];

                                appProps.hasPermissionToAccessMainAccount =
                                    responseData?.hasPermissionToAccessMainAccount;
                                appProps.currentUserInstalledTheExtension =
                                    responseData?.currentUserInstalledTheExtension;
                                appProps.isAgentStatusEnabled = responseData?.isAgentStatusEnabled;
                                appProps.isAiFeaturesEnabled = responseData?.isAiFeaturesEnabled === true;
                                appProps.guideAgentEnabled = responseData?.guideAgentEnabled === true;
                                appProps.guideJourneyEnabled = responseData?.guideJourneyEnabled === true;

                                if (responseData?.subAccounts) {
                                    appProps.subAccountsList = responseData?.subAccounts;
                                }
                                if (
                                    hasUrlSearchParam("osyncId") &&
                                    getUrlSearchParam("osyncId") === "" &&
                                    !appProps?.hasPermissionToAccessMainAccount
                                ) {
                                    setPermissionDeniedToSubAccount(true);
                                    return;
                                }
                                if (
                                    window.self !== window.top &&
                                    localStorage.getItem("recent-accessed-sub-account") &&
                                    !hasUrlSearchParam("osyncId")
                                ) {
                                    reloadWindow(urlParams, localStorage.getItem("recent-accessed-sub-account"));
                                    return;
                                }

                                if (
                                    !appProps?.isParentOsyncAccount &&
                                    appProps?.osyncId &&
                                    !hasUrlSearchParam("osyncId")
                                ) {
                                    reloadWindow(urlParams, appProps?.osyncId);
                                    return;
                                }

                                let leftServiceDetails = responseData?.leftService;

                                if (leftServiceDetails) {
                                    appProps.leftServiceName = leftServiceDetails?.name;
                                    appProps.leftServiceDisplayName = leftServiceDetails?.displayName;
                                }
                                let leftAuthDetails = responseData?.left;
                                if (leftAuthDetails) {
                                    appProps.authCRM = leftAuthDetails?.auth?.authorized;
                                }

                                if (integrationDetails) {
                                    if (integrationDetails?.userInfo?.userId) {
                                        appProps.userLogo = integrationDetails?.userInfo?.userLogo;
                                        appProps.userId =
                                            integrationDetails?.userInfo?.userId ?? getUrlSearchParam("userId");
                                        appProps.userName = integrationDetails?.userInfo?.userName;
                                        if (!appProps?.companyId) {
                                            appProps.companyId = integrationDetails?.userInfo?.companyId;
                                        }
                                        appProps.currentUserEmail = integrationDetails?.userInfo?.userEmail;
                                        appProps.emailId = integrationDetails?.email;
                                        appProps.domain = integrationDetails?.domain;
                                        appProps.isOmniAccount = integrationDetails?.omniAccount;
                                        appProps.phone = integrationDetails?.phone;
                                    } else if (getUrlSearchParam("userId")) {
                                        appProps.userId = getUrlSearchParam("userId");
                                    }

                                    if (integrationDetails?.email) {
                                        let email = integrationDetails?.email; // Changed const to let
                                        email = decodeURIComponent(email);
                                        try {
                                            window.pagesense = window.pagesense || [];
                                            window.pagesense.push(["identifyUser", email]);
                                        } catch (error) {}
                                    }

                                    if (integrationDetails?.leftServiceId) {
                                        appProps.leftServiceId = integrationDetails?.leftServiceId;
                                    }
                                }
                                let hasAccessToCurrentSubAccount = false;

                                if (licenseObject?.allLicensedUserDetails) {
                                    appProps.allLicensedUserDetails = licenseObject?.allLicensedUserDetails;

                                    let userLicense = licenseObject?.allLicensedUserDetails.filter(
                                        (item) => item.remoteUserId === appProps.userId
                                    );
                                    if (userLicense && userLicense.length > 0) {
                                        hasAccessToCurrentSubAccount = true;
                                    }
                                }
                                if (!hasAccessToCurrentSubAccount) {
                                    setPermissionDeniedToSubAccount(true);
                                    return;
                                }
                                appProps.fetchData = fetchData;

                                appProps.phoneList = true;

                                const responseInteg = responseData?.integrations?.filter((item) => item?.integProps);

                                if (responseInteg) {
                                    let installedServicesResp = response.data;

                                    let rightServices = installedServicesResp?.integrations.filter(
                                        (obj) => obj?.integProps
                                    );
                                    if (rightServices?.length > 0) {
                                        appProps.serviceInstalled = true;
                                        appProps.installedApps = rightServices;
                                        let randomIntegId = rightServices.filter(
                                            (obj) => obj?.right?.service_name !== "hello_send"
                                        )?.[0]?.integProps?.integId;
                                        if (!randomIntegId) {
                                            randomIntegId = rightServices?.[0]?.integProps?.integId;
                                        }
                                        appProps.randomIntegId = randomIntegId;
                                    } else {
                                        appProps.serviceInstalled = false;
                                    }
                                    if (installedServicesResp) {
                                        const licenseObject = installedServicesResp?.license;
                                        appProps.licenseObj = licenseObject;
                                        appProps.savedNumbers = installedServicesResp?.numberTeamNameMap
                                            ? installedServicesResp?.numbers.map((number) => ({
                                                  ...number,
                                                  teamName:
                                                      installedServicesResp?.numberTeamNameMap[number.phoneNumber],
                                              }))
                                            : installedServicesResp?.numbers;
                                        appProps.allNumbers = installedServicesResp?.allNumbers;
                                    }

                                    if (responseInteg && responseInteg?.length > 0) {
                                        for (const authorizeData of responseInteg) {
                                            appProps.integId = authorizeData.integProps.integId;
                                            let leftAuth = authorizeData?.left?.auth?.authorized;
                                            appProps.authCRM = authorizeData?.left?.auth?.authorized;
                                            if (!leftAuth) {
                                                let leftServiceName = authorizeData?.left?.service_display_name;
                                                let leftService = capitalizeFirstLetter(leftServiceName);
                                                appProps.authTitle =
                                                    serviceObject?.authModal?.title ?? "Restore Access";
                                                appProps.authContent =
                                                    serviceObject?.authModal?.content ??
                                                    "Authorize " + leftService + " to Reconnect HelloSend.";
                                                appProps.authButton = "Authorize";
                                                appProps.authLink = authorizeData?.left?.auth?.url;
                                                appProps.authorizeObj = authorizeData;
                                                break;
                                            }
                                            const allAuthorizedFalse = responseInteg.every(
                                                (authorizeData) => !authorizeData?.right?.auth?.authorized
                                            );
                                            if (allAuthorizedFalse) {
                                                let rightAuth = authorizeData?.right?.auth?.authorized;
                                                if (!rightAuth) {
                                                    appProps.authTitle = "Service not Authorized";
                                                    appProps.authContent =
                                                        "Restore SMS channel access by authorizing SMS Channel.";
                                                    appProps.authButton = "Authorize";
                                                    appProps.authLink = authorizeData?.right?.auth?.url;
                                                    appProps.authorizeObj = authorizeData;
                                                    break;
                                                }
                                            }
                                        }
                                    } else if (leftAuth) {
                                        let leftServiceName = leftAuth?.service_display_name;
                                        let leftService = capitalizeFirstLetter(leftServiceName);
                                        appProps.authTitle = serviceObject?.authModal?.title ?? "Restore Access";
                                        appProps.authContent =
                                            serviceObject?.authModal?.content ??
                                            "Authorize " + leftService + " to Reconnect HelloSend.";
                                        appProps.authButton = "Authorize";
                                        appProps.authLink = leftAuth?.auth?.url;
                                        appProps.authorizeObj = { ...(appProps?.authorizeObj || {}), left: leftAuth };
                                    }
                                    const user = getCurrentUserDetails(appProps);
                                    appProps.hideChannels = user?.phoneAccess === 2 && user?.roleId === 13;
                                }
                                appProps.isAppContextCreated = true;
                                appProps.setLoading(false);
                            }
                            setAppProperties((prev) => ({ ...prev, ...appProps }));
                            setAppPropsObject(appProps);
                        })
                        .catch(function () {
                            setLoading(false);
                        });
                });
            });
        }
    }, [isEmpty(serviceObject), appProperties?.RE_INIT, initFunctionCall]);

    return <AppContext.Provider value={appPropertiesObject}>{props.children}</AppContext.Provider>;
};

export async function getController(serviceName) {
    const { ParentController } = await import("../controller/ParentController.js");
    const parentControllerObj = Object.assign({}, ParentController);

    if (serviceName === "pdOmni") {
        const { PipeDriveSMS } = await import("../controller/PipedriveController.js");
        return Object.assign({}, parentControllerObj, PipeDriveSMS);
    }
    let serviceObj = {};
    if (serviceName === "zohodesk" || serviceName === "zdesk_external") {
        const { ZohoSupportDesk } = await import("../controller/ZohodeskExternalController.js");
        serviceObj = Object.assign({}, parentControllerObj, ZohoSupportDesk);
        if (serviceName === "zohodesk") {
            const ZohoDeskEmbedded = (await import("../controller/ZohoDeskEmbeddedController.js")).default;
            serviceObj = Object.assign(serviceObj, ZohoDeskEmbedded);
        }
        return serviceObj;
    }
    if (serviceName === "zendesk" || serviceName === "zendesk_external") {
        const { ZenDesk_External } = await import("../controller/zendesk/ZenDeskExternalController.js");
        let serviceObj = Object.assign({}, parentControllerObj, ZenDesk_External);
        if (serviceName === "zendesk") {
            const { ZenDesk_Embedded } = await import("../controller/zendesk/ZenDeskEmbeddedController.js");
            serviceObj = Object.assign(serviceObj, ZenDesk_Embedded);
        }
        return serviceObj;
    }
    if (serviceName === "default") {
        const { DefaultOmessage } = await import("../controller/DefaultOmessageController.js");
        return Object.assign({}, parentControllerObj, DefaultOmessage);
    }
    if (serviceName === "hubspotcrm") {
        const { Hubspot } = await import("../controller/HubspotController.js");
        return Object.assign({}, parentControllerObj, Hubspot);
    }
    if (serviceName === "zohocrm" || serviceName === "zohocrmredirect") {
        const ZohoCRM = (await import("../controller/ZohoCRMController.js")).default;
        let serviceObj = Object.assign({}, parentControllerObj, ZohoCRM);
        if (serviceName === "zohocrm") {
            const ZohoCRMEmbedded = (await import("../controller/ZohoCRMEmbeddedController.js")).default;
            serviceObj = Object.assign(serviceObj, ZohoCRMEmbedded);
        }
        return serviceObj;
    }
    if (serviceName === "zohobigin" || serviceName === "zohobiginredirect") {
        const ZohoBigin = (await import("../controller/ZohoBiginController.js")).default;
        let serviceObj = Object.assign({}, parentControllerObj, ZohoBigin);
        if (serviceName === "zohobigin") {
            const ZohoBiginEmbedded = (await import("../controller/ZohoBiginEmbeddedController.js")).default;
            serviceObj = Object.assign(serviceObj, ZohoBiginEmbedded);
        }
        return serviceObj;
    }
    if (serviceName === "zohobooks" || serviceName === "zohobooksredirect") {
        const ZohoBooks = (await import("../controller/zohobooks/ZohoBooksController.js")).default;
        let serviceObj = Object.assign({}, parentControllerObj, ZohoBooks);
        if (serviceName === "zohobooks") {
            const ZohoBooksEmbedded = (await import("../controller/zohobooks/ZohoBooksEmbeddedController.js")).default;
            serviceObj = Object.assign(serviceObj, ZohoBooksEmbedded);
        }
        return serviceObj;
    }
    if (serviceName === "teams") {
        const { HelloSend } = await import("../controller/HelloSendController.js");
        const { TeamsController } = await import("../controller/teamsController.js");
        return { ...parentControllerObj, ...HelloSend, ...TeamsController };
    }
    if (serviceName === "hellosend") {
        const { HelloSend } = await import("../controller/HelloSendController.js");
        return Object.assign({}, parentControllerObj, HelloSend);
    }
    if (serviceName === "salesforce") {
        const { Salesforce } = await import("../controller/SalesforceController.js");
        return Object.assign({}, parentControllerObj, Salesforce);
    }
    if (serviceName === "hubspot") {
        const { Hubspot } = await import("../controller/HubspotController.js");
        return Object.assign({}, parentControllerObj, Hubspot);
    }
    if (serviceName === "wix") {
        const { Wix } = await import("../controller/WixController.js");
        return Object.assign({}, parentControllerObj, Wix);
    }
    if (serviceName === "shopify") {
        const { Shopify } = await import("../controller/ShopifyController.js");
        return Object.assign({}, parentControllerObj, Shopify);
    }
    if (serviceName === "gohighlevel") {
        const { GoHighLevel } = await import("../controller/GoHighLevelController.js");
        return Object.assign({}, parentControllerObj, GoHighLevel);
    }
    return serviceObj;
}

function isEmpty(obj) {
    for (var x in obj) {
        return false;
    }
    return true;
}

function reloadWindow(urlParams, osyncId) {
    urlParams.append("osyncId", osyncId);
    var newUrl =
        window.location.protocol + "//" + window.location.host + window.location.pathname + "?" + urlParams.toString();
    window.location.href = newUrl;
}
